// © Microsoft Corporation. All rights reserved.

import {
    BaseExperienceConfig,
    ChildExperienceConfigInfo,
    ChildExperienceValidator,
    ChildInfo,
    ChildrenTypes,
    CustomCompositionConfig,
    ExperienceCompositionTypes,
    GridCompositionConfig,
    HorizontalStackConfig,
    LayoutConfig,
    PeregrineChildInfo,
    ScreenTypeMetadata,
    ScreenWidth,
    ValidationArgs,
    ValidatorSectionName,
    VerticalStackConfig
} from "@msnews/core";
import {
    ColumnArrangement,
    ExperienceInitialProps,
    fastBreakpointManager
} from "@msnews/experiences-base";

import { AppValidator } from "./AppValidator";
import { cloneDeep } from "lodash-es";
import { isFunction } from "util";

/**
 * Config Processor class to filter config
 * @class
 */
export class ConfigProcessor {
    /**
     * experience validator map to be provided by app type. Each entry has a validator name and a validator function associated
     * with that.
     */
    static expValidatorMap: Map<string, (args?: ValidationArgs) => boolean> = new Map();

    /**
     * config map that keeps track of original configs which are modified by ConfigProcessor.
     * This is required for partial page refresh scenarios where original config is required for refernce like ReAdding previously removed SD cards
     */
    static expConfigMap: Map<string, BaseExperienceConfig<any>> = new Map();

    /**
     * Registers apptype validators
     * @method
     * @param {AppValidator} apptypeValidator Validator for a specific app type
     * @returns {void}
     */
    public static registerAppValidator(apptypeValidator: AppValidator): void {
        this.expValidatorMap = apptypeValidator.getAllValidators();
    }

    /**
     * process config based on registered validators for current app-type and removes child experience from
     * section defined in CMS condig
     * @method
     * @param {BaseExperienceConfig<any>} expConfig Experience config object
     * @returns {BaseExperienceConfig<any>} returns filtered experience config
     */
    public static processConfig(expConfig: BaseExperienceConfig<any>, namespace: string): BaseExperienceConfig<any> {
        if (!this.expValidatorMap || this.expValidatorMap.size < 1 || !expConfig || !expConfig.childExpValidatorMap) {
            return expConfig;
        }

        const expMap: { [instanceId: string]: ChildExperienceValidator } = expConfig.childExpValidatorMap;
        let originalConfig: BaseExperienceConfig<any> = null;
        let tempConfig: BaseExperienceConfig<any> = null;

        for (const validatorName in expMap) {
            const childExpValidator = expMap[validatorName];
            if (this.expValidatorMap.has(validatorName)) {
                const validator = this.expValidatorMap.get(validatorName);
                const removeChildExp = validator(childExpValidator.validationArgs);

                if (removeChildExp) {
                    if (tempConfig === null) {
                        // always use saved config if exists
                        tempConfig = this.expConfigMap.get(namespace) || expConfig;
                    }

                    // keep a copy of original config
                    if (originalConfig === null) {
                        originalConfig = cloneDeep(tempConfig);
                    }

                    expConfig = this.filterConfig(childExpValidator, tempConfig);
                }
            }
        }

        if (originalConfig) {
            this.expConfigMap.set(namespace, originalConfig);
        } else if (tempConfig === null && this.expConfigMap.has(namespace)) {
            // if tempConfig is null, none of the validators returned true.
            // restore expConfig with originalConfig for this case
            expConfig = this.expConfigMap.get(namespace);
        }

        return expConfig;
    }

    /**
     * remove child experience based on the response from registerd validator and config.
     * @method
     * @param {ChildExperienceValidator} childExpValidator Experience config object
     * @param {BaseExperienceConfig<any>} expConfig Experience config object
     * @returns {BaseExperienceConfig<any>} returns filtered experience config
     */
    private static removeFromRiverGridChildren(childExpValidator: ChildExperienceValidator, expConfig: any): BaseExperienceConfig<any> {
        // TODO: River config is in experiences and we should not take dependency of experiences in this project.
        // We should move RiverConfig to core config project or expConfig has to be of type any
        const riverGridChildren = expConfig.riverGridChildren;
        if (riverGridChildren) {
            riverGridChildren.forEach(element => {
                const childElems = element.children;
                if (childElems) {
                    element.children = childElems.filter((childExp) => {
                        const configInfo = childExp.childExperienceConfigInfo;
                        return !configInfo || !childExpValidator.childExperienceConfigInfo
                        || childExpValidator.childExperienceConfigInfo.instanceId !== configInfo.instanceId
                        || childExpValidator.childExperienceConfigInfo.experienceType !== configInfo.experienceType;
                    });
                }
            });
        }

        return expConfig;
    }

    /**
     * Given a experience config, get a list of child experience configs for child data connectors
     * @method
     * @param {BaseExperienceConfig<any>} baseExperienceConfig experience config to find child data connectors configs
     * @returns {ChildExperienceConfigInfo[]} - child data connectors configs
     */
    public static getChildrenDataConnectorConfigInfos(baseExperienceConfig: BaseExperienceConfig<any>): ChildExperienceConfigInfo[] {
        const childrenConfigInfo: ChildExperienceConfigInfo[] = [];

        if (baseExperienceConfig && baseExperienceConfig.dataConnectors && baseExperienceConfig.dataConnectors.length > 0) {
            const dataConnectorForCurrentScreenType = ConfigProcessor.getOrdinalObjectForCurrentBreakpoint<ChildExperienceConfigInfo>(baseExperienceConfig.dataConnectors);

            if (dataConnectorForCurrentScreenType && dataConnectorForCurrentScreenType.children && dataConnectorForCurrentScreenType.children.length > 0) {
                dataConnectorForCurrentScreenType.children.forEach(connector => {
                    childrenConfigInfo.push(connector);
                });
            }
        }

        return childrenConfigInfo;
    }

    /**
     * Helper function to match screenWidth and return the correct ScreenTypeMetadata object
     * @method
     * @param {ScreenWidth} screenWidth - screenWidth to match
     * @param {ScreenTypeMetadata[]} screenTypeObjList - Array of ScreenTypeMetadata objects
     * @returns {ScreenTypeMetadata} returns the matching ScreenTypeMetadata object for given screenWidth.
     */
    public static getMetadataByScreenWidth<T>(screenWidth: ScreenWidth, screenTypeObjList: ScreenTypeMetadata<T>[]): ScreenTypeMetadata<T> {
        const result =
            screenTypeObjList.find(obj =>
                obj.screenWidth === screenWidth
            )
            || screenTypeObjList.find(obj =>
                obj.screenWidth === ScreenWidth.Any
            );

        return result;
    }

    /**
     * This method returns the ScreenTypeMetadata for current breakpoint.
     * @method
     * @param {ScreenTypeMetadata<T>[]} screenTypeObjList Array of ScreenTypeMetadata objects
     * @returns {ScreenTypeMetadata<T>} returns the matching ScreenTypeMetadata object for current breakpoint.
     */
    public static getOrdinalObjectForCurrentBreakpoint<T>(screenTypeObjList: ScreenTypeMetadata<T>[]): ScreenTypeMetadata<T> {
        switch (fastBreakpointManager().currentColumnArrangement) {
            case ColumnArrangement.c1: {
                return ConfigProcessor.getMetadataByScreenWidth(ScreenWidth.OneColumn, screenTypeObjList);
            }
            case ColumnArrangement.c2: {
                return ConfigProcessor.getMetadataByScreenWidth(ScreenWidth.TwoColumn, screenTypeObjList);
            }
            case ColumnArrangement.c3: {
                return ConfigProcessor.getMetadataByScreenWidth(ScreenWidth.ThreeColumn, screenTypeObjList);
            }
            case ColumnArrangement.c4: {
                return ConfigProcessor.getMetadataByScreenWidth(ScreenWidth.FourColumn, screenTypeObjList);
            }
            case ColumnArrangement.c5: {
                return ConfigProcessor.getMetadataByScreenWidth(ScreenWidth.FiveColumn, screenTypeObjList);
            }
            default: {
                return ConfigProcessor.getMetadataByScreenWidth(ScreenWidth.Any, screenTypeObjList);
            }
        }
    }

    /**
     * Composition resolver. It will return the composition value based on any possible overrides.
     * @method
     * @param {BaseExperienceConfig<any>} baseExperienceConfig Array of ScreenTypeMetadata objects
     * @param {ExperienceInitialProps} connectorProps initial props for connector
     * @returns { GridCompositionConfig | LayoutConfig | HorizontalStackConfig | VerticalStackConfig | CustomCompositionConfig } returns composition from config or given override
     */
    public static compositionOverrideResolver(baseExperienceConfig: BaseExperienceConfig<any>, connectorProps?: ExperienceInitialProps): GridCompositionConfig | LayoutConfig | HorizontalStackConfig | VerticalStackConfig | CustomCompositionConfig {
        const { getCompositionIdOverride } = connectorProps || {};
        const { composition, compositionOverrides } = baseExperienceConfig;
        return compositionOverrides && isFunction(getCompositionIdOverride) && compositionOverrides[getCompositionIdOverride()] || composition;
    }

    /**
     * Give an experience config, get a list of child experience configs.
     * @method
     * @param {BaseExperienceConfig<any>} baseExperienceConfig experience config to find child data connectors configs
     * @param {ExperienceInitialProps} connectorProps connector props for given experience
     * @returns {ChildExperienceConfigInfo[]} - child data connectors configs
     */
    public static getChildrenExperienceConfigInfos(baseExperienceConfig: BaseExperienceConfig<any>, connectorProps?: ExperienceInitialProps): ChildExperienceConfigInfo[] {
        const childrenConfigInfo: ChildExperienceConfigInfo[] = [];

        // Determine if there are any children in the composition portion of the config document.
        if (baseExperienceConfig && baseExperienceConfig.composition) {

            const composition = ConfigProcessor.compositionOverrideResolver(baseExperienceConfig, connectorProps);

            switch (composition.type) {
                case ExperienceCompositionTypes.horizontalStack:
                case ExperienceCompositionTypes.layout: {

                    // Iterate over the composition config to find the children
                    const layoutConfig = composition as LayoutConfig;

                    // Iterate over the composition config to find the children
                    layoutConfig.page.children.forEach(grid => {

                        // If the grid does not contain children, then there are no child experiences
                        if (grid.children === undefined) {
                            return;
                        }

                        // Iterate over the grid children (each one is a column) and see if there is a child experience
                        grid.children.forEach((column) => {

                            // If the columnProps does not contain children, then there are no child experiences
                            if (!column.children) {
                                return;
                            }

                            // If the column.children is a legacy ColumnChildInfo, or of type PeregrineExperience, then
                            // add the child to the list
                            const { childType } = column.children as ChildInfo;
                            if (!childType) {
                                childrenConfigInfo.push(column.children as ChildExperienceConfigInfo);
                            } else if (childType === ChildrenTypes.peregrineExperience) {
                                childrenConfigInfo.push((column.children as PeregrineChildInfo).child);
                            }
                        });
                    });
                    break;
                }

                case ExperienceCompositionTypes.grid: {

                    // Iterate over the grid composition config to find the peregrine child experiences to load
                    const gridRows = (composition as GridCompositionConfig).grid.rows;
                    gridRows.forEach(row => {

                        // If the row does not contain columns, then there are no child experiences
                        if (row.columns === undefined) {
                            return;
                        }

                        // Iterate over the grid columns and see if there are any items
                        row.columns.forEach((column) => {
                            // If the column.item is a PeregrineExperience, then add the child to the list of children experiences to load
                            const { itemType } = column.item;
                            if (itemType && itemType === ChildrenTypes.peregrineExperience) {
                                childrenConfigInfo.push(column.item.child as ChildExperienceConfigInfo);
                            }
                        });
                    });
                    break;
                }

                case ExperienceCompositionTypes.customExperience: {
                    const customConfig = composition as CustomCompositionConfig;
                    childrenConfigInfo.push(customConfig.experience);

                    break;
                }

                // No children to load, if we do not know what type of composition this experience has
                default:
            }
        }

        return childrenConfigInfo;
    }

    /**
     * Verify if the section provided in CMS schema is supported for removal of child experiences.
     * Currently only riverGridChildren is supported. Support will be extended to other sections.
     * @method
     * @param {ChildExperienceValidator} childExpValidator Experience config object
     * @param {BaseExperienceConfig<any>} expConfig Experience config object
     * @returns {BaseExperienceConfig<any>} returns filtered experience config
     */
    private static filterConfig(childExpValidator: ChildExperienceValidator, expConfig: BaseExperienceConfig<any>): BaseExperienceConfig<any> {
        if (childExpValidator && typeof childExpValidator.sectionName === "string" && childExpValidator.sectionName.toLowerCase() === ValidatorSectionName.riverGridChildren.toLowerCase()) {
            return this.removeFromRiverGridChildren(childExpValidator, expConfig);
        }

        return expConfig;
    }

    /** Resets validator map, needed to handle multiple requests for SSR */
    public static resetValidatorMap(): void {
        this.expValidatorMap.clear();
    }

    /** Resets config map, needed to handle multiple requests for SSR */
    public static resetConfigMap(): void {
        this.expConfigMap.clear();
    }
}